import React from "react";
import {
  Button,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ElectricBoltOutlinedIcon from "@mui/icons-material/ElectricBoltOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import ArrowForwardIosOutlinedIcon from "@mui/icons-material/ArrowForwardIosOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import RadarIcon from "@mui/icons-material/Radar";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import moment from "moment-timezone";
import ContentWrapper from "../Layout/ContentWrapper";
import { CollapsableCard, CountdownTimer } from "../customComponents";
import { useCountdown } from "../../hooks/useCountdown";

const Rules = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMedium = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  // Scoring weights/maxima are API-provided (derived from the `rounds` table),
  // so these cards track a manual `rounds` edit with no code change.
  const scoring = useSelector((state) => state.app?.scoring);
  const w = scoring?.weights;
  const isFull = scoring?.contest_type === "World Cup";

  // Picks deadline = the contest's first match, from
  // worldcup_2026.contests.play_starts_at (surfaced via /status) — NOT a
  // hardcoded date. Parsed as UTC (stored times are UTC) and handed to the
  // countdown as unix seconds, so editing the contest's start in the table
  // moves this clock with no deploy.
  const playStartsAtRaw = useSelector((state) => state.app?.playStartsAt);
  const playStartsAt = playStartsAtRaw ? moment.utc(playStartsAtRaw).unix() : 0;
  const [days, hours, minutes, seconds] = useCountdown(
    playStartsAt,
    true,
    true,
  );

  // Summary scoring card — one row per round, weights straight from `rounds`.
  const pointsBullets = w
    ? [
        ...(isFull ? [{ title: "Group Stage", points: `${w.group_stage}` }] : []),
        { title: "Round of 16", points: `${w.r16}` },
        { title: "Quarterfinals", points: `${w.qf}` },
        { title: "Semifinals", points: `${w.sf}` },
        { title: "Third Place", points: `${w.third}` },
        { title: "Final", points: `${w.final}` },
      ]
    : [];

  const detailPointsBullets = w
    ? [
        ...(isFull
          ? [
              { title: "Group Stage Pick", points: `${w.group_stage}` },
              { title: "3rd Place Ranking", points: `${w.group_stage}` },
            ]
          : []),
        { title: "Round of 16", points: `${w.r16}` },
        { title: "Quarterfinals", points: `${w.qf}` },
        { title: "Semifinals", points: `${w.sf}` },
        { title: "Third Place", points: `${w.third}` },
        { title: "Final Winner", points: `${w.final}` },
      ]
    : [];

  const qAndA = [
    {
      question: "Can I edit picks later?",
      answer: isFull
        ? "You can edit your picks up until the deadline for each phase. Group stage picks lock when the first match begins. Knockout picks lock before each round starts."
        : "You can edit your picks up until the deadline for each round. Knockout picks lock before each round starts.",
    },
    {
      question: "How are ties handled?",
      answer: isFull
        ? "Ties are broken by: (1) Most knockout points, (2) Most group stage points, (3) Earliest submission time, (4) Random draw."
        : "Ties are broken by: (1) Most knockout points, (2) Earliest submission time, (3) Random draw.",
    },
    {
      question: "When are winners announced?",
      answer:
        "Winners are announced within 7 days after the tournament final. Prize distribution begins within 30 days of announcement.",
    },
    {
      question: "Is participation free?",
      answer:
        "Yes! The World Cup Bracket Challenge is completely free to enter. No purchase necessary.",
    },
  ];

  return (
    <ContentWrapper>
      <Grid container>
        <Grid
          container
          direction="column"
          sx={{
            maxWidth: theme.breakpoints.values.lg,
            justifyContent: "center",
            gap: "48px",
          }}
        >
          {/* header */}
          <Grid
            container
            direction="column"
            sx={{
              padding: "16px 32px 16px",
              justifyContent: "center",
              alignItems: "center",
              maxWidth: "1088px",
              gap: "48px",
            }}
          >
            <Grid
              container
              direction="column"
              sx={{
                padding: "0px 32px 16px",
                justifyContent: "center",
                alignItems: "center",
                maxWidth: "1088px",
                gap: "40px",
              }}
            >
              {/* trophy circle icon */}
              <Grid
                container
                sx={[
                  {
                    width: isMobile ? "80px" : "120px",
                    height: isMobile ? "80px" : "120px",
                    padding: "0px 8px",
                    borderRadius: "100%",
                    background: `linear-gradient(135deg, ${theme.branding.cyan[300]} 0%, ${theme.branding.cyan[700]} 100%)`,
                    boxShadow: `0px 8px 20px 0px  ${theme.branding.cyan[600]}`,
                    justifyContent: "center",
                    alignItems: "center",
                  },
                  (theme) =>
                    theme.applyStyles("dark", {
                      boxShadow: `0px 8px 20px 0px  ${theme.branding.cyan[800]}`,
                    }),
                ]}
              >
                <EmojiEventsOutlinedIcon
                  sx={{
                    height: isMobile ? "36px" : "44px",
                    width: isMobile ? "36px" : "44px",
                    color: theme.branding.gray[0],
                    alignSelf: "center",
                  }}
                />
              </Grid>

              {/* text and buttons */}
              <Grid
                container
                direction="column"
                sx={{
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "20px",
                }}
              >
                {/* title text */}
                <Grid
                  container
                  direction="column"
                  sx={{
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "20px",
                  }}
                >
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: isMobile ? "32px" : "72px",
                      textAlign: "center",
                      lineHeight: 1,
                    }}
                  >
                    World Cup Bracket{" "}
                    <span style={{ color: theme.branding.cyan[500] }}>
                      Rules
                    </span>
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: isMobile ? "14px" : "24px",
                      textAlign: "center",
                      color: theme.branding.gray[400],
                    }}
                  >
                    Predict every match. Climb the leaderboard. Win exclusive
                    rewards.
                  </Typography>
                </Grid>

                {/* buttons */}
                <Grid
                  container
                  direction="row"
                  wrap="nowrap"
                  sx={{ gap: "8px" }}
                >
                  <Button
                    contained
                    onClick={() => {
                      navigate(isFull ? "/picks/group" : "/picks/knockout");
                    }}
                    endIcon={
                      <ArrowForwardOutlinedIcon
                        sx={{ color: "inherit", height: "18px", width: "18px" }}
                      />
                    }
                    sx={{
                      padding: isMobile ? "16px" : "16px 32px",
                      borderRadius: "10px",
                      color: theme.branding.gray[0],
                      background: `linear-gradient(75deg, ${theme.branding.cyan[500]} 0%, ${theme.branding.cyan[300]} 80%)`,
                    }}
                  >
                    <Typography
                      variant={isMobile ? "subtitle1" : "h5"}
                      sx={{ color: theme.branding.gray[0] }}
                    >
                      Make Your Picks
                    </Typography>
                  </Button>
                </Grid>
              </Grid>

              {/* countdown timer */}
              <Grid
                container
                direction="column"
                sx={{ gap: "16px", justifyContent: "center" }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "14px",
                    textTransform: "uppercase",
                    color: theme.branding.gray[500],
                    textAlign: "center",
                  }}
                >
                  Picks Deadline
                </Typography>
                <CountdownTimer
                  days={days}
                  hours={hours}
                  minutes={minutes}
                  seconds={isMobile ? null : seconds}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* row two: 4 boxes */}
          <Grid container direction="row" spacing={2}>
            {/* blue */}
            <Grid
              container
              direction="column"
              size={{ xs: 12, sm: 6, md: 3 }}
              sx={[
                {
                  gap: "16px",
                  padding: "24px",
                  borderRadius: "16px",
                  border: `1px solid ${theme.branding.gray[200]}`,
                  backgroundColor: theme.branding.gray[0],
                  transition: "all 0.5s ease-out",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 10px 20px -10px #33DDEE33`, //cyan[300] 20% opacity
                    borderColor: theme.branding.cyan[500],
                  },
                },
                (theme) =>
                  theme.applyStyles("dark", {
                    border: `1px solid ${theme.branding.darkBlue[600]}`,
                    backgroundColor: theme.branding.darkBlue[800],
                  }),
              ]}
            >
              <Grid
                container
                sx={{
                  borderRadius: "14px",
                  height: "48px",
                  width: "48px",
                  justifyContent: "center",
                  alignItems: "center",
                  background: `linear-gradient(135deg, ${theme.branding.cyan[300]} 0%,  ${theme.branding.cyan[700]} 100%);`,
                }}
              >
                <EmojiEventsOutlinedIcon
                  sx={{
                    color: theme.branding.gray[0],
                    height: "24px",
                    width: "24px",
                  }}
                />
              </Grid>
              <Grid container direction="column" sx={{ gap: "12px" }}>
                <Typography variant="h4">How It Works</Typography>
                <Grid container direction="column" sx={{ gap: "2px" }}>
                  <Grid
                    container
                    direction="row"
                    wrap="nowrap"
                    sx={{ gap: "4px" }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: theme.branding.cyan[500] }}
                    >
                      •
                    </Typography>{" "}
                    <Typography
                      variant="body2"
                      sx={{ color: theme.branding.gray[500] }}
                    >
                      Pick winners for every stage
                    </Typography>
                  </Grid>
                  <Grid
                    container
                    direction="row"
                    wrap="nowrap"
                    sx={{ gap: "4px" }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: theme.branding.cyan[500] }}
                    >
                      •
                    </Typography>{" "}
                    <Typography
                      variant="body2"
                      sx={{ color: theme.branding.gray[500] }}
                    >
                      Submit before kickoff
                    </Typography>
                  </Grid>
                  <Grid
                    container
                    direction="row"
                    wrap="nowrap"
                    sx={{ gap: "4px" }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: theme.branding.cyan[500] }}
                    >
                      •
                    </Typography>{" "}
                    <Typography
                      variant="body2"
                      sx={{ color: theme.branding.gray[500] }}
                    >
                      Earn points each round
                    </Typography>
                  </Grid>
                  <Grid
                    container
                    direction="row"
                    wrap="nowrap"
                    sx={{ gap: "4px" }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: theme.branding.cyan[500] }}
                    >
                      •
                    </Typography>{" "}
                    <Typography
                      variant="body2"
                      sx={{ color: theme.branding.gray[500] }}
                    >
                      Highest score wins
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* pink */}
            <Grid
              container
              direction="column"
              size={{ xs: 12, sm: 6, md: 3 }}
              sx={[
                {
                  gap: "16px",
                  padding: "24px",
                  borderRadius: "16px",
                  border: `1px solid ${theme.branding.gray[200]}`,
                  backgroundColor: theme.branding.gray[0],
                  transition: "all 0.5s ease-out",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 10px 20px -10px #F6339A33`, //hotPink[500] 20% opacity
                    borderColor: theme.branding.hotPink[500],
                  },
                },
                (theme) =>
                  theme.applyStyles("dark", {
                    border: `1px solid ${theme.branding.darkBlue[600]}`,
                    backgroundColor: theme.branding.darkBlue[800],
                  }),
              ]}
            >
              <Grid
                container
                sx={{
                  borderRadius: "14px",
                  height: "48px",
                  width: "48px",
                  justifyContent: "center",
                  alignItems: "center",
                  background: `linear-gradient(135deg, ${theme.branding.purple[500]} 0%,  ${theme.branding.hotPink[500]} 100%);`,
                }}
              >
                <ElectricBoltOutlinedIcon
                  sx={{
                    color: theme.branding.gray[0],
                    height: "24px",
                    width: "24px",
                  }}
                />
              </Grid>
              <Grid container direction="column" sx={{ gap: "12px" }}>
                <Typography variant="h4">Scoring System</Typography>
                <Grid container direction="column" sx={{ gap: "2px" }}>
                  {pointsBullets.map((bull, index) => (
                    <Grid
                      key={index}
                      container
                      direction="row"
                      wrap="nowrap"
                      sx={{ gap: "4px", justifyContent: "space-between" }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: theme.branding.gray[500] }}
                      >
                        {bull.title}
                      </Typography>{" "}
                      <Typography
                        variant="body2"
                        sx={{
                          background: `linear-gradient(135deg, ${theme.branding.purple[500]} 0%, ${theme.branding.hotPink[500]} 100%)`,
                          backgroundClip: "text",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          fontWeight: 700,
                        }}
                      >
                        + {bull.points}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>

            {/* orange */}
            <Grid
              container
              direction="column"
              size={{ xs: 12, sm: 6, md: 3 }}
              sx={[
                {
                  gap: "16px",
                  padding: "24px",
                  borderRadius: "16px",
                  border: `1px solid ${theme.branding.gray[200]}`,
                  backgroundColor: theme.branding.gray[0],
                  transition: "all 0.5s ease-out",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 10px 20px -10px #FF980033`, //orange[300] 20% opacity
                    borderColor: theme.branding.orange[500],
                  },
                },
                (theme) =>
                  theme.applyStyles("dark", {
                    border: `1px solid ${theme.branding.darkBlue[600]}`,
                    backgroundColor: theme.branding.darkBlue[800],
                  }),
              ]}
            >
              <Grid
                container
                sx={{
                  borderRadius: "14px",
                  height: "48px",
                  width: "48px",
                  justifyContent: "center",
                  alignItems: "center",
                  background: `linear-gradient(135deg, ${theme.branding.yellow[500]} 0%,  ${theme.branding.orange[500]} 100%);`,
                }}
              >
                <RadarIcon
                  sx={{
                    color: theme.branding.gray[0],
                    height: "24px",
                    width: "24px",
                  }}
                />
              </Grid>
              <Grid container direction="column" sx={{ gap: "12px" }}>
                <Typography variant="h4">Tiebreakers</Typography>
                <Grid container direction="column">
                  {[
                    "Total goals predicted",
                    "Earliest Submission",
                    "Most knockout picks",
                  ].map((title, index) => (
                    <Grid
                      key={index}
                      container
                      direction="row"
                      wrap="nowrap"
                      sx={{ gap: "4px" }}
                    >
                      <Grid
                        container
                        sx={{
                          height: "20px",
                          width: "20px",
                          minWidth: "20px",
                          borderRadius: "100%",
                          backgroundColor: "#ff990020", //orange[500] 10% opacity
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={[
                            {
                              color: theme.branding.yellow[500],
                              lineHeight: 1,
                            },
                            (theme) =>
                              theme.applyStyles("dark", {
                                color: theme.branding.yellow[500],
                              }),
                          ]}
                        >
                          {index + 1}
                        </Typography>{" "}
                      </Grid>
                      <Typography
                        variant="body2"
                        sx={{ color: theme.branding.gray[500] }}
                      >
                        {title}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>

            {/* red */}
            <Grid
              container
              direction="column"
              size={{ xs: 12, sm: 6, md: 3 }}
              sx={[
                {
                  gap: "16px",
                  padding: "24px",
                  borderRadius: "16px",
                  border: `1px solid ${theme.branding.red[300]}`,
                  backgroundColor: theme.branding.gray[0],
                  transition: "all 0.5s ease-out",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 10px 20px -10px #FB2C3633`, //red[500] 20% opacity
                    borderColor: theme.branding.red[400],
                  },
                },
                (theme) =>
                  theme.applyStyles("dark", {
                    border: `1px solid ${theme.branding.darkBlue[600]}`,
                    backgroundColor: theme.branding.darkBlue[800],
                  }),
              ]}
            >
              <Grid
                container
                sx={{
                  borderRadius: "14px",
                  height: "48px",
                  width: "48px",
                  justifyContent: "center",
                  alignItems: "center",
                  background: `linear-gradient(135deg, ${theme.branding.red[500]} 0%,  #EC003F 100%);`, //custom red that dont fit in with tone of red for theme
                }}
              >
                <AccessTimeOutlinedIcon
                  sx={{
                    color: theme.branding.gray[0],
                    height: "24px",
                    width: "24px",
                  }}
                />
              </Grid>
              <Grid container direction="column" sx={{ gap: "12px" }}>
                <Typography variant="h4">Deadlines</Typography>
                <Grid container direction="column">
                  {[
                    "Picks lock at match kickoff",
                    "Knockout rounds reopen after",
                    "No edits after lock",
                  ].map((title, index) => (
                    <Grid
                      key={index}
                      container
                      direction="row"
                      wrap="nowrap"
                      sx={{ gap: "4px" }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: theme.branding.red[400] }}
                      >
                        •
                      </Typography>{" "}
                      <Typography
                        variant="body2"
                        sx={{ color: theme.branding.gray[500] }}
                      >
                        {title}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid
            container
            direction={isMedium ? "column" : "row"}
            wrap="nowrap"
            spacing={4}
          >
            <Grid
              container
              direction="column"
              size={{ xs: 12, md: 8 }}
              sx={{ gap: "24px" }}
            >
              {/* rewards and prizes  */}
              <Grid
                container
                direction="column"
                sx={[
                  {
                    gap: "32px",
                    padding: "32px",
                    width: "100%",
                    borderRadius: "16px",
                    border: `1px solid ${theme.branding.gray[200]}`,
                    backgroundColor: theme.branding.gray[0],
                  },
                  (theme) =>
                    theme.applyStyles("dark", {
                      border: `1px solid ${theme.branding.darkBlue[600]}`,
                      backgroundColor: theme.branding.darkBlue[800],
                    }),
                ]}
              >
                <Grid
                  container
                  direction="row"
                  wrap="nowrap"
                  sx={{ gap: "16px", alignItems: "center" }}
                >
                  <Grid
                    container
                    sx={{
                      borderRadius: "14px",
                      height: "48px",
                      width: "48px",
                      justifyContent: "center",
                      alignItems: "center",
                      background: `linear-gradient(135deg, ${theme.branding.gold[300]} 0%,  ${theme.branding.gold[500]} 100%);`, //custom red that dont fit in with tone of red for theme
                    }}
                  >
                    <WorkspacePremiumOutlinedIcon
                      sx={{
                        color: theme.branding.gray[0],
                        height: "24px",
                        width: "24px",
                      }}
                    />
                  </Grid>
                  <Typography variant="h4" sx={{ fontSize: "24px" }}>
                    Rewards & Prizes
                  </Typography>
                </Grid>
                <Grid
                  container
                  direction={isMobile ? "column" : "row"}
                  spacing={2}
                >
                  {/* 1st place */}
                  <Grid
                    container
                    direction="column"
                    size={{ sm: 12, md: 4 }}
                    sx={{
                      padding: "24px",
                      gap: "12px",
                      borderRadius: "16px",
                      border: `1px solid ${theme.branding.gold[500]}`,
                      position: "relative",
                    }}
                  >
                    <Typography variant="h2">&#127942;</Typography>
                    <Grid container direction="column" sx={{ gap: "8px" }}>
                      <Typography variant="h5">1st Place</Typography>
                      <Typography
                        variant="body2"
                        sx={[
                          {
                            color: theme.branding.gold[300],
                          },
                          (theme) =>
                            theme.applyStyles("dark", {
                              color: theme.branding.gold[200],
                            }),
                        ]}
                      >
                        Spotlight Premium + FIFA Jersey
                      </Typography>
                    </Grid>
                    {/* placeholder grid for transparent background */}
                    <Grid
                      sx={{
                        height: "100%",
                        width: "100%",
                        borderRadius: "16px",
                        backgroundColor: theme.branding.gold[500],
                        opacity: "5%",
                        position: "absolute",
                        top: 0,
                        left: 0,
                      }}
                    />
                  </Grid>
                  {/* top ten */}
                  <Grid
                    container
                    direction="column"
                    size={{ sm: 12, md: 4 }}
                    sx={{
                      padding: "24px",
                      gap: "12px",
                      borderRadius: "16px",
                      border: `1px solid ${theme.branding.cyan[500]}`,
                      position: "relative",
                    }}
                  >
                    <Typography variant="h2">&#127903;</Typography>
                    <Grid container direction="column" sx={{ gap: "8px" }}>
                      <Typography variant="h5">Top 10</Typography>
                      <Typography
                        variant="body2"
                        sx={[
                          {
                            color: theme.branding.cyan[500],
                          },
                          (theme) =>
                            theme.applyStyles("dark", {
                              color: theme.branding.cyan[200],
                            }),
                        ]}
                      >
                        Exclusive giveaways
                      </Typography>
                    </Grid>
                    {/* placeholder grid for transparent background */}
                    <Grid
                      sx={{
                        height: "100%",
                        width: "100%",
                        borderRadius: "16px",
                        backgroundColor: theme.branding.cyan[500],
                        opacity: "5%",
                        position: "absolute",
                        top: 0,
                        left: 0,
                      }}
                    />
                  </Grid>
                  {/* participation */}
                  <Grid
                    container
                    direction="column"
                    size={{ sm: 12, md: 4 }}
                    sx={{
                      padding: "24px",
                      gap: "12px",
                      borderRadius: "16px",
                      border: `1px solid ${theme.branding.purple[500]}`,
                      position: "relative",
                    }}
                  >
                    <Typography variant="h2">&#9917;</Typography>
                    <Grid container direction="column" sx={{ gap: "8px" }}>
                      <Typography variant="h5">Participation</Typography>
                      <Typography
                        variant="body2"
                        sx={[
                          {
                            color: theme.branding.purple[400],
                          },
                          (theme) =>
                            theme.applyStyles("dark", {
                              color: theme.branding.purple[200],
                            }),
                        ]}
                      >
                        Digital Achievement Badge
                      </Typography>
                    </Grid>
                    {/* placeholder grid for transparent background */}
                    <Grid
                      sx={{
                        height: "100%",
                        width: "100%",
                        borderRadius: "16px",
                        background: `linear-gradient(135deg, ${theme.branding.purple[500]} 0%, ${theme.branding.hotPink[500]} 100%)`,
                        opacity: "5%",
                        position: "absolute",
                        top: 0,
                        left: 0,
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* detailed scoring  */}
              <Grid
                container
                direction="column"
                sx={[
                  {
                    gap: "24px",
                    padding: "32px",
                    width: "100%",
                    borderRadius: "16px",
                    border: `1px solid ${theme.branding.gray[200]}`,
                    backgroundColor: theme.branding.gray[0],
                  },
                  (theme) =>
                    theme.applyStyles("dark", {
                      border: `1px solid ${theme.branding.darkBlue[600]}`,
                      backgroundColor: theme.branding.darkBlue[800],
                    }),
                ]}
              >
                <Grid
                  container
                  direction="row"
                  wrap="nowrap"
                  sx={{ gap: "16px", alignItems: "center" }}
                >
                  <Grid
                    container
                    sx={{
                      borderRadius: "14px",
                      height: "48px",
                      width: "48px",
                      justifyContent: "center",
                      alignItems: "center",
                      background: `linear-gradient(135deg, ${theme.branding.purple[500]} 0%,  ${theme.branding.hotPink[500]} 100%);`,
                    }}
                  >
                    <RadarIcon
                      sx={{
                        color: theme.branding.gray[0],
                        height: "24px",
                        width: "24px",
                      }}
                    />
                  </Grid>
                  <Typography variant="h4" sx={{ fontSize: "24px" }}>
                    Detailed Scoring
                  </Typography>
                </Grid>
                <Grid container direction="column" sx={{ gap: "12px" }}>
                  {detailPointsBullets.map((item) => (
                    <Grid
                      container
                      direction="row"
                      wrap="nowrap"
                      sx={[
                        {
                          width: "100%",
                          padding: "16px",
                          gap: "4px",
                          borderRadius: "14px",
                          border: `1px solid ${theme.branding.gray[200]}`,
                          backgroundColor: theme.branding.gray[50],
                          justifyContent: "space-between",
                          alignItems: "center",
                          "&:hover": {
                            borderColor: theme.branding.cyan[300],
                          },
                        },
                        (theme) =>
                          theme.applyStyles("dark", {
                            border: `1px solid ${theme.branding.darkBlue[600]}`,
                            backgroundColor: theme.branding.darkBlue[900],
                          }),
                      ]}
                    >
                      <Typography variant="subtitle1">{item.title}</Typography>
                      <Grid
                        container
                        direction="row"
                        wrap="nowrap"
                        sx={{ gap: "8px", alignItems: "center" }}
                      >
                        <Typography
                          variant="h4"
                          sx={{
                            fontSize: "24px",
                            background: `linear-gradient(135deg, ${theme.branding.cyan[300]} 40%, ${theme.branding.cyan[500]} 80%, ${theme.branding.cyan[700]} 100%)`,
                            backgroundClip: "text",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                          }}
                        >
                          {item.points}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: theme.branding.gray[500] }}
                        >
                          pt{item.pts !== "1" && "s"}
                        </Typography>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
                <Grid
                  container
                  sx={[
                    {
                      width: "100%",
                      padding: "16px",
                      gap: "4px",
                      borderRadius: "14px",
                      border: `1px solid ${theme.branding.cyan[300]}`,
                      backgroundColor: theme.branding.gray[50],
                      justifyContent: "space-between",
                      position: "relative",
                      "&:hover": {
                        borderColor: theme.branding.cyan[500],
                      },
                    },
                    (theme) =>
                      theme.applyStyles("dark", {
                        backgroundColor: theme.branding.darkBlue[900],
                      }),
                  ]}
                >
                  <Typography variant="body2">
                    <strong>Maximum Score: </strong>
                    {scoring?.total_max ?? 0} points
                    {isFull
                      ? ` (${scoring?.group_top2_max ?? 0} group + ${
                          scoring?.third_max ?? 0
                        } wildcard + ${scoring?.ko_max ?? 0} knockout)`
                      : ` (${scoring?.ko_max ?? 0} knockout)`}
                  </Typography>
                  <Grid
                    sx={{
                      height: "100%",
                      width: "100%",
                      position: "absolute",
                      top: 0,
                      left: 0,
                      borderRadius: "14px",
                      background: `linear-gradient(90deg, ${theme.branding.cyan[500]} 0%, transparent 100%)`,
                      opacity: "10%",
                    }}
                  />
                </Grid>
              </Grid>

              {/* tiebreakers */}
              <Grid
                container
                direction="column"
                sx={[
                  {
                    gap: "24px",
                    padding: "32px",
                    width: "100%",
                    borderRadius: "16px",
                    border: `1px solid ${theme.branding.gray[200]}`,
                    backgroundColor: theme.branding.gray[0],
                  },
                  (theme) =>
                    theme.applyStyles("dark", {
                      border: `1px solid ${theme.branding.darkBlue[600]}`,
                      backgroundColor: theme.branding.darkBlue[800],
                    }),
                ]}
              >
                <Grid
                  container
                  direction="row"
                  wrap="nowrap"
                  sx={{ gap: "16px", alignItems: "center" }}
                >
                  <Grid
                    container
                    sx={{
                      borderRadius: "14px",
                      height: "48px",
                      width: "48px",
                      justifyContent: "center",
                      alignItems: "center",
                      background: `linear-gradient(135deg, ${theme.branding.yellow[500]} 0%,  ${theme.branding.orange[500]} 100%);`,
                    }}
                  >
                    <RadarIcon
                      sx={{
                        color: theme.branding.gray[0],
                        height: "24px",
                        width: "24px",
                      }}
                    />
                  </Grid>
                  <Typography variant="h4" sx={{ fontSize: "24px" }}>
                    Tiebreakers
                  </Typography>
                </Grid>
                <Grid container direction="column" sx={{ gap: "0px" }}>
                  {[
                    "Most knockout points (later rounds matter more)",
                    ...(isFull ? ["Most group stage points"] : []),
                    "Earliest bracket submission",
                    "Random draw",
                  ].map((title, index) => (
                    <Grid
                      key={`tiebreakers-detailed-${index}`}
                      container
                      direction="row"
                      wrap="nowrap"
                      sx={{
                        gap: "12px",
                        alignItems: isMobile ? "flex-start" : "center",
                      }}
                    >
                      <Grid
                        container
                        sx={{
                          height: "20px",
                          width: "20px",
                          minWidth: "20px",
                          borderRadius: "100%",
                          backgroundColor: theme.branding.cyan[300],
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.branding.gray[0],
                            lineHeight: 1,
                            alignSelf: "center",
                          }}
                        >
                          {index + 1}
                        </Typography>{" "}
                      </Grid>
                      <Typography
                        variant="body1"
                        sx={{ color: theme.branding.gray[500] }}
                      >
                        {title}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              {/* deadlines */}
              <Grid
                container
                direction="column"
                sx={[
                  {
                    gap: "24px",
                    padding: "32px",
                    width: "100%",
                    borderRadius: "16px",
                    border: `1px solid ${theme.branding.gray[200]}`,
                    backgroundColor: theme.branding.gray[0],
                  },
                  (theme) =>
                    theme.applyStyles("dark", {
                      border: `1px solid ${theme.branding.darkBlue[600]}`,
                      backgroundColor: theme.branding.darkBlue[800],
                    }),
                ]}
              >
                <Grid
                  container
                  direction="row"
                  wrap="nowrap"
                  sx={{ gap: "16px", alignItems: "center" }}
                >
                  <Grid
                    container
                    sx={{
                      borderRadius: "14px",
                      height: "48px",
                      width: "48px",
                      justifyContent: "center",
                      alignItems: "center",
                      background: `linear-gradient(135deg, ${theme.branding.red[500]} 0%,  #EC003F 100%);`, //custom red that dont fit in with tone of red for theme,
                    }}
                  >
                    <AccessTimeOutlinedIcon
                      sx={{
                        color: theme.branding.gray[0],
                        height: "24px",
                        width: "24px",
                      }}
                    />
                  </Grid>
                  <Typography variant="h4" sx={{ fontSize: "24px" }}>
                    Deadlines
                  </Typography>
                </Grid>
                <Grid container direction="column" sx={{ gap: "12px" }}>
                  {[
                    // [`Group stage picks`, ` lock one week after selection`],
                    [`Knockout picks`, ` lock before each round begins`],
                    [`Late submissions`, ` are not accepted`],
                  ].map(([boldPart, rest], index) => (
                    <Grid
                      key={`tiebreakers-detailed-${index}`}
                      container
                      direction="row"
                      wrap="nowrap"
                      sx={{
                        gap: "12px",
                        alignItems: "center",
                      }}
                    >
                      <ArrowForwardIosOutlinedIcon
                        container
                        sx={{
                          height: "20px",
                          width: "20px",
                          minWidth: "20px",
                          color: theme.branding.cyan[500],
                        }}
                      />
                      <Typography
                        variant="body1"
                        sx={{ color: theme.branding.gray[500] }}
                      >
                        <strong>{boldPart}</strong>{rest}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              {/* fair play */}
              <Grid
                container
                direction="column"
                sx={[
                  {
                    gap: "24px",
                    padding: "32px",
                    width: "100%",
                    borderRadius: "16px",
                    border: `1px solid ${theme.branding.gray[200]}`,
                    backgroundColor: theme.branding.gray[0],
                  },
                  (theme) =>
                    theme.applyStyles("dark", {
                      border: `1px solid ${theme.branding.darkBlue[600]}`,
                      backgroundColor: theme.branding.darkBlue[800],
                    }),
                ]}
              >
                <Grid
                  container
                  direction="row"
                  wrap="nowrap"
                  sx={{ gap: "16px", alignItems: "center" }}
                >
                  <Grid
                    container
                    sx={{
                      borderRadius: "14px",
                      height: "48px",
                      width: "48px",
                      justifyContent: "center",
                      alignItems: "center",
                      background: `linear-gradient(135deg, ${theme.branding.green[500]} 0%,  ${theme.branding.green[700]} 100%);`,
                    }}
                  >
                    <ShieldOutlinedIcon
                      sx={{
                        color: theme.branding.gray[0],
                        height: "24px",
                        width: "24px",
                      }}
                    />
                  </Grid>
                  <Typography variant="h4" sx={{ fontSize: "24px" }}>
                    Fair Play
                  </Typography>
                </Grid>
                <Grid container direction="column" sx={{ gap: "12px" }}>
                  {[
                    "One account per participant",
                    "No automated entries",
                    "SpotlightNews reserves the right to remove fraudulent brackets",
                  ].map((title, index) => (
                    <Grid
                      key={`tiebreakers-detailed-${index}`}
                      container
                      direction="row"
                      wrap="nowrap"
                      sx={{
                        gap: "12px",
                        alignItems: "center",
                      }}
                    >
                      <ArrowForwardIosOutlinedIcon
                        container
                        sx={{
                          height: "20px",
                          width: "20px",
                          minWidth: "20px",
                          color: theme.branding.cyan[500],
                        }}
                      />
                      <Typography
                        variant="body1"
                        sx={{ color: theme.branding.gray[500] }}
                      >
                        {title}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              {/* FAQ */}
              <Grid
                container
                direction="column"
                sx={[
                  {
                    gap: "24px",
                    padding: "32px",
                    width: "100%",
                    maxWidth: "100%",
                    borderRadius: "16px",
                    border: `1px solid ${theme.branding.gray[200]}`,
                    backgroundColor: theme.branding.gray[0],
                  },
                  (theme) =>
                    theme.applyStyles("dark", {
                      border: `1px solid ${theme.branding.darkBlue[600]}`,
                      backgroundColor: theme.branding.darkBlue[800],
                    }),
                ]}
              >
                <Typography variant="h4" sx={{ fontSize: "24px" }}>
                  Frequently Asked Questions
                </Typography>
                <Grid container direction="column" sx={{ gap: "12px" }}>
                  {qAndA.map(({ question, answer }, index) => (
                    <Grid container sx={{ maxWidth: "565px" }}>
                      <CollapsableCard
                        title={question}
                        customArrowColor={theme.branding.cyan[500]}
                        customArrowColorDark={theme.branding.cyan[300]}
                      >
                        <Typography variant="body1">{answer}</Typography>
                      </CollapsableCard>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>

            {/* leaderboard  */}
            <Grid
              container
              direction="column"
              size={{ xs: 12, md: 4 }}
              sx={[
                {
                  gap: "24px",
                  width: "100%",
                  maxWidth: "100%",
                  height: "fit-content",
                  borderRadius: "16px",
                  border: `1px solid ${theme.branding.gray[200]}`,
                  backgroundColor: theme.branding.gray[0],
                },
                (theme) =>
                  theme.applyStyles("dark", {
                    border: `1px solid ${theme.branding.darkBlue[600]}`,
                    backgroundColor: theme.branding.darkBlue[800],
                  }),
              ]}
            >
              <Grid
                container
                direction="row"
                wrap="nowrap"
                sx={[
                  {
                    padding: "20px 20px 16px",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    borderBottom: `1px solid ${theme.branding.gray[200]}`,
                  },
                  (theme) =>
                    theme.applyStyles("dark", {
                      borderColor: theme.branding.darkBlue[600],
                    }),
                ]}
              >
                <Grid
                  container
                  direction="row"
                  wrap="nowrap"
                  sx={{ gap: "8px", alignItems: "center" }}
                >
                  <TrendingUpRoundedIcon
                    sx={{
                      height: "20px",
                      width: "20px",
                      color: theme.branding.cyan[300],
                    }}
                  />
                  <Typography variant="h5">Live Leaderboard</Typography>
                </Grid>
                {/* live leaderboard indicator */}
                <Grid
                  sx={{
                    height: "8px",
                    width: "8px",
                    maxWidth: "8px",
                    backgroundColor: theme.branding.gray[500],
                    borderRadius: "100%",
                  }}
                />
              </Grid>
              <Grid
                container
                direction="column"
                sx={{ gap: "16px", padding: "12px 24px 24px" }}
              >
                <Grid
                  container
                  direction="column"
                  sx={{ gap: "12px", minHeight: "300px" }}
                >
                  {/* update to include leaderboard */}
                  <Grid
                    container
                    sx={{
                      heght: "300px",
                      justifyContent: "center",
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.branding.gray[500],
                        marginTop: "24px",
                      }}
                    >
                      Leaderboard coming soon
                    </Typography>
                  </Grid>
                </Grid>
                <Button
                  variant="outlined"
                  disabled
                  onClick={() => navigate("/leaderboard")}
                  endIcon={<ArrowForwardOutlinedIcon />}
                  sx={{ padding: "12px 16px", borderRadius: "14px" }}
                >
                  View Full Leaderboard
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </ContentWrapper>
  );
};

export default Rules;
